import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeftIcon, PrinterIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../hooks/useAuth';
import { ScopedLink } from '../../components/ScopedLink';
import { useToast } from '../../components/Toast';
import { staffDisplayName } from './staff-display-name';
import {
  useGetStaffComplianceQuery,
  useVerifyCredentialMutation,
  useGetAttachmentUrlMutation,
} from '../../store/api/localApi';
import { CredentialType, VettingStatus } from '../../types';
import VettingForm from '../../components/staff-compliance/VettingForm';
import CredentialForm from '../../components/staff-compliance/CredentialForm';
import ReferenceForm from '../../components/staff-compliance/ReferenceForm';

/**
 * One staff member's full compliance profile — the product's first
 * per-staff-member detail page (Team.tsx manages everyone through inline
 * modals over a flat list; there is no other per-member route to copy). See
 * StudentDetail.tsx for the house detail-page pattern this follows.
 *
 * THREE THINGS THIS PAGE MUST GET RIGHT (see task-17-brief CONTROLLER
 * ADDENDUM):
 *
 * 1. Vetting is APPEND-ONLY. History renders newest-first and no past
 *    vetting row ever gets an edit affordance — correcting one means
 *    recording a new one via "Record vetting", never editing history.
 *
 * 2. A staff member viewing their OWN profile may add a credential (it lands
 *    unverified) but may not add vetting and may not verify anything,
 *    including their own credential. The server enforces this regardless of
 *    what the UI sends — `requireStaffComplianceOwner` never grants
 *    `staff-compliance:manage` for a self target — so this page simply
 *    doesn't render the controls that would be refused.
 *
 * 3. Neutral copy throughout: "Not recorded" for anything absent, no red, no
 *    alarm styling, no warning icons. Most real profiles are mostly empty —
 *    that's normal, not a wall of failures.
 */

const VETTING_LABEL: Record<VettingStatus, string> = {
  [VettingStatus.NOT_RECORDED]: 'Not recorded',
  [VettingStatus.IN_PROGRESS]: 'In progress',
  [VettingStatus.VETTED]: 'Vetted',
  [VettingStatus.REVET_DUE]: 'Re-vet due',
};

const CREDENTIAL_TYPE_LABEL: Record<string, string> = {
  [CredentialType.FIRST_AID]: 'First Aid',
  [CredentialType.CHILD_PROTECTION]: 'Child Protection',
  [CredentialType.QUALIFICATION]: 'Qualification',
  [CredentialType.FIRE_SAFETY]: 'Fire Safety',
  [CredentialType.MANUAL_HANDLING]: 'Manual Handling',
  [CredentialType.OTHER]: 'Other',
};

function credentialTypeLabel(type: string): string {
  return CREDENTIAL_TYPE_LABEL[type] ?? type;
}

/**
 * Attachment link — mints a FRESH 15-minute presigned URL per click, never
 * prefetched and never cached in state beyond the click that opened it (a
 * cached URL outlives the authorization that granted it). Opens in a new tab
 * so the click that triggered the mint is the same click that consumes it.
 */
const AttachmentLink: React.FC<{ staffUserId: string; recordId: string; testId: string }> = ({
  staffUserId,
  recordId,
  testId,
}) => {
  const { showError } = useToast();
  const [getAttachmentUrl, { isLoading }] = useGetAttachmentUrlMutation();

  const handleClick = async () => {
    try {
      const result = await getAttachmentUrl({ staffUserId, recordId }).unwrap();
      window.open(result.url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      showError(err.data?.error || 'Failed to open attachment');
    }
  };

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={handleClick}
      disabled={isLoading}
      className="text-sm text-primary-600 hover:text-primary-800 disabled:opacity-50"
    >
      {isLoading ? 'Opening...' : 'View attachment'}
    </button>
  );
};

const StaffComplianceProfile: React.FC = () => {
  const { staffUserId } = useParams<{ staffUserId: string }>();
  const { user, isAdmin } = useAuth();
  const { showSuccess, showError } = useToast();

  const [vettingFormOpen, setVettingFormOpen] = useState(false);
  const [credentialFormOpen, setCredentialFormOpen] = useState(false);
  const [referenceFormOpen, setReferenceFormOpen] = useState(false);

  const {
    data: profile,
    isLoading,
    isError,
  } = useGetStaffComplianceQuery(staffUserId ?? '', {
    skip: !staffUserId,
  });
  const [verifyCredential, { isLoading: isVerifying }] = useVerifyCredentialMutation();

  if (!staffUserId) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">
        Invalid URL
      </div>
    );
  }

  // Self vs admin is derived the same way the server derives it — from the
  // logged-in user's id compared against the route's staffUserId — never
  // trusted from anywhere else. A caller who is neither the target nor an
  // admin gets refused by the server (403); this page has nothing special to
  // render for that case beyond the loading/empty states below.
  const isSelf = user?.id === staffUserId;
  const canManageVetting = isAdmin && !isSelf;
  const canAddCredential = isAdmin || isSelf;
  const canVerify = isAdmin && !isSelf;
  const canAddReference = isAdmin && !isSelf;

  if (isLoading) {
    return (
      <div className="py-10 text-center text-sm text-gray-500">Loading compliance profile…</div>
    );
  }

  // The server is the real gate (requireStaffComplianceOwner refuses a
  // colleague's profile with a 403) — this just makes sure a refused request
  // doesn't leave the profile SHELL rendered (empty sections, testid intact)
  // for someone the server just said no to. A staff member hitting a
  // colleague's URL directly must see nothing recognizable as this page.
  //
  // `staff-compliance-denied` is a DISTINCT, dedicated testid (not merely
  // the absence of `staff-compliance-profile`) so a test can assert this
  // branch is what actually rendered — a positive assertion here can't pass
  // vacuously the way `toHaveCount(0)` on the loaded-view testid could (that
  // count is also 0 while the query is simply loading, or if this whole
  // guard were deleted and the branch never taken for an unrelated reason).
  if (isError || !profile) {
    return (
      <div
        data-testid="staff-compliance-denied"
        className="min-h-screen flex items-center justify-center text-sm text-slate-500"
      >
        You don&apos;t have access to this page.
      </div>
    );
  }

  const vetting = [...(profile?.vetting ?? [])].sort((a, b) =>
    b.createdAt.localeCompare(a.createdAt)
  );
  const credentials = profile?.credentials ?? [];
  const references = profile?.references ?? [];
  const staffName = staffDisplayName(profile);

  const handleVerify = async (credentialId: string) => {
    try {
      await verifyCredential({ staffUserId, credentialId }).unwrap();
      showSuccess('Credential verified');
    } catch (err: any) {
      showError(err.data?.error || 'Failed to verify credential');
    }
  };

  return (
    <div className="py-6" data-testid="staff-compliance-profile">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        <div className="flex items-center justify-between">
          {/* Only admins have the staff-compliance register to return to. A staff
              member viewing their own record would land on an access-denied page,
              so hide the link for them (empty spacer keeps Print right-aligned). */}
          {isAdmin ? (
            <ScopedLink
              to="/staff-compliance"
              data-testid="staff-compliance-profile-back"
              className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Back to register
            </ScopedLink>
          ) : (
            <div />
          )}
          <ScopedLink
            to={`/staff/${staffUserId}/compliance/print`}
            data-testid="profile-print-link"
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <PrinterIcon className="h-4 w-4" aria-hidden="true" />
            Print
          </ScopedLink>
        </div>

        <div>
          <h1 className="text-2xl font-bold text-gray-900">{staffName || 'Compliance profile'}</h1>
          <p className="mt-1 text-sm text-gray-600">
            Garda vetting, credentials, and reference checks for this staff member. Add records as
            you go — nothing here is required to use the rest of the app.
          </p>
        </div>

        {/* ---- Vetting ---- */}
        <section className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">Garda vetting</h2>
              {canManageVetting && (
                <button
                  type="button"
                  data-testid="vetting-add"
                  onClick={() => setVettingFormOpen(true)}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                >
                  Record vetting
                </button>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Current status: {VETTING_LABEL[profile?.vettingStatus ?? VettingStatus.NOT_RECORDED]}
            </p>

            {vetting.length === 0 ? (
              <p className="mt-4 text-sm text-gray-500">Not recorded</p>
            ) : (
              // Newest-first, append-only history. No edit affordance is ever
              // rendered on a past row — correcting one means recording a
              // new one via "Record vetting" above.
              <ul className="mt-4 divide-y divide-gray-100">
                {vetting.map((v) => (
                  <li
                    key={v.vettingId}
                    data-testid={`vetting-history-row-${v.vettingId}`}
                    className="py-3"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">
                        {VETTING_LABEL[v.vettingStatus]}
                      </span>
                      {v.disclosureDate && (
                        <span className="text-xs text-gray-500">Disclosure {v.disclosureDate}</span>
                      )}
                    </div>
                    <dl className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                      {v.nvbReference && (
                        <div>
                          <dt className="inline font-medium">NVB reference: </dt>
                          <dd className="inline">{v.nvbReference}</dd>
                        </div>
                      )}
                      {v.vettedThrough && (
                        <div>
                          <dt className="inline font-medium">Vetted through: </dt>
                          <dd className="inline">{v.vettedThrough}</dd>
                        </div>
                      )}
                      {v.decision && (
                        <div>
                          <dt className="inline font-medium">Decision: </dt>
                          <dd className="inline">{v.decision}</dd>
                        </div>
                      )}
                      {v.decidedByName && (
                        <div>
                          <dt className="inline font-medium">Decided by: </dt>
                          <dd className="inline">{v.decidedByName}</dd>
                        </div>
                      )}
                      {v.revetDueAt && (
                        <div>
                          <dt className="inline font-medium">Re-vet due: </dt>
                          <dd className="inline">{v.revetDueAt}</dd>
                        </div>
                      )}
                      <div>
                        <dt className="inline font-medium">Recorded by: </dt>
                        <dd className="inline">{v.createdByName}</dd>
                      </div>
                    </dl>
                    {v.attachmentS3Key && (
                      <div className="mt-2">
                        <AttachmentLink
                          staffUserId={staffUserId}
                          recordId={v.vettingId}
                          testId={`vetting-attachment-${v.vettingId}`}
                        />
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* ---- Credentials ---- */}
        <section className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">Credentials</h2>
              {canAddCredential && (
                <button
                  type="button"
                  data-testid="credential-add"
                  onClick={() => setCredentialFormOpen(true)}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                >
                  Add credential
                </button>
              )}
            </div>

            {credentials.length === 0 ? (
              <p className="mt-4 text-sm text-gray-500">Not recorded</p>
            ) : (
              <ul className="mt-4 divide-y divide-gray-100">
                {credentials.map((c) => (
                  <li
                    key={c.credentialId}
                    data-testid={`credential-row-${c.credentialId}`}
                    className="py-3"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium text-gray-900">{c.title}</span>
                        <span className="ml-2 text-xs text-gray-500">
                          {credentialTypeLabel(c.credentialType)}
                        </span>
                      </div>
                      <span className="text-xs text-gray-600">
                        {c.verified ? 'Verified' : 'Not yet verified'}
                      </span>
                    </div>
                    <dl className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                      {c.issuer && (
                        <div>
                          <dt className="inline font-medium">Issuer: </dt>
                          <dd className="inline">{c.issuer}</dd>
                        </div>
                      )}
                      {c.awardedDate && (
                        <div>
                          <dt className="inline font-medium">Awarded: </dt>
                          <dd className="inline">{c.awardedDate}</dd>
                        </div>
                      )}
                      {c.expiryDate && (
                        <div>
                          <dt className="inline font-medium">Expires: </dt>
                          <dd className="inline">{c.expiryDate}</dd>
                        </div>
                      )}
                      {c.verified && c.verifiedByName && (
                        <div>
                          <dt className="inline font-medium">Verified by: </dt>
                          <dd className="inline">{c.verifiedByName}</dd>
                        </div>
                      )}
                    </dl>
                    <div className="mt-2 flex items-center gap-4">
                      {c.attachmentS3Key && (
                        <AttachmentLink
                          staffUserId={staffUserId}
                          recordId={c.credentialId}
                          testId={`credential-attachment-${c.credentialId}`}
                        />
                      )}
                      {!c.verified && canVerify && (
                        <button
                          type="button"
                          data-testid={`credential-verify-${c.credentialId}`}
                          onClick={() => handleVerify(c.credentialId)}
                          disabled={isVerifying}
                          className="text-sm text-primary-600 hover:text-primary-800 disabled:opacity-50"
                        >
                          Verify
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* ---- References ---- */}
        <section className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">References</h2>
              {canAddReference && (
                <button
                  type="button"
                  data-testid="reference-add"
                  onClick={() => setReferenceFormOpen(true)}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                >
                  Add reference
                </button>
              )}
            </div>

            {references.length === 0 ? (
              <p className="mt-4 text-sm text-gray-500">Not recorded</p>
            ) : (
              <ul className="mt-4 divide-y divide-gray-100">
                {references.map((r) => (
                  <li
                    key={r.referenceId}
                    data-testid={`reference-row-${r.referenceId}`}
                    className="py-3"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">{r.refereeName}</span>
                      <span className="text-xs text-gray-500">
                        Validated {r.validatedAt.slice(0, 10)}
                      </span>
                    </div>
                    <dl className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                      {r.refereeOrganisation && (
                        <div>
                          <dt className="inline font-medium">Organisation: </dt>
                          <dd className="inline">{r.refereeOrganisation}</dd>
                        </div>
                      )}
                      <div>
                        <dt className="inline font-medium">Validated by: </dt>
                        <dd className="inline">{r.validatedByName}</dd>
                      </div>
                      {r.note && (
                        <div className="col-span-2">
                          <dt className="inline font-medium">Note: </dt>
                          <dd className="inline">{r.note}</dd>
                        </div>
                      )}
                    </dl>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>

      {canManageVetting && (
        <VettingForm
          isOpen={vettingFormOpen}
          onClose={() => setVettingFormOpen(false)}
          staffUserId={staffUserId}
        />
      )}
      {canAddCredential && (
        <CredentialForm
          isOpen={credentialFormOpen}
          onClose={() => setCredentialFormOpen(false)}
          staffUserId={staffUserId}
          isSelf={isSelf}
        />
      )}
      {canAddReference && (
        <ReferenceForm
          isOpen={referenceFormOpen}
          onClose={() => setReferenceFormOpen(false)}
          staffUserId={staffUserId}
        />
      )}
    </div>
  );
};

export default StaffComplianceProfile;
